import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { signupApi } from "../model/authApi";
import { useNavigate } from "react-router-dom";
import {
  validateNameWithMessage,
  validateEmailWithMessage,
  validatePasswordWithMessage,
  validatePasswordMatch,
  validateSignupCredentials,
} from "../utils/auth.utils";
import type { SignupCredentials, SignupApiPayload } from "../model/authTypes";
import { routes } from "@/app/navigation/routes";
// import {
//   PHONE_DIAL_CODE_OPTIONS,
// } from "@/features/auth/constants/phoneDialCodes";

/** Same rules as live mobile field validation; used on submit without depending on `mobileError` state. */
const getSignupPhoneFormatError = (phone: string): string | null => {
  if (!phone) return null;
  if (!/^\+[0-9]+$/.test(phone)) return "Must start with + and digits only";
  const digits = phone.slice(1);
  if (digits.length < 10) return "Minimum 10 digits required";
  if (digits.length > 12) return "Maximum 12 digits allowed";
  return null;
};

/**
 * Signup ViewModel
 * Manages signup form state and business logic
 */
export const useSignupViewModel = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<SignupCredentials>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
    phone: "",
  });

  const [dialCode, setDialCode] = useState("+91");
  const [nationalNumber, setNationalNumber] = useState("");

  const [mobileError, setMobileError] = useState<string | null>(null);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const BUTTON_ID = "signup-button";

  const updateNationalNumber = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    setNationalNumber(digitsOnly);
  };

  const mutation = useMutation({
    mutationFn: (data: SignupCredentials) => {
      const apiData: SignupApiPayload = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        phone: fullPhone,
        role: 2,
        referal_code: "",
      };
      return signupApi(apiData, "inline", BUTTON_ID);
    },
    // onSuccess: (_data, variables) => {
    //   navigate(routes.emailVerificationPending, {
    //     state: {
    //       email: variables.email,
    //       from: (location.state as { from?: string })?.from,
    //     },
    //   });
    // },
    onSuccess: (_data, variables) => {
      sessionStorage.setItem("otpFlow", "signup");
      // store phone
      sessionStorage.setItem("phone", fullPhone);

      // store email (needed later)
      sessionStorage.setItem("email", variables.email);

      // ✅ FIRST go to OTP
      navigate(routes.phoneVerification, {
        state: {
          phone: fullPhone,
        },
      });
    },
  });

  // const updateMobile = useCallback((phone: string) => {
  //   // Allow only + and digits
  //   if (/^\+?[0-9]*$/.test(phone) && phone.length <= 13) {
  //     setCredentials((prev) => ({ ...prev, phone }));

  //     if (phone) {
  //       setMobileError(getSignupPhoneFormatError(phone));
  //     } else {
  //       setMobileError(null);
  //     }
  //   }
  // }, []);

  const updateFirstName = useCallback((firstName: string) => {
    setCredentials((prev) => ({ ...prev, firstName }));
    // Validate first name on change
    if (firstName) {
      const validation = validateNameWithMessage(firstName, "First name");
      setFirstNameError(validation.error || null);
    } else {
      setFirstNameError(null);
    }
  }, []);

  const updateLastName = useCallback((lastName: string) => {
    setCredentials((prev) => ({ ...prev, lastName }));
    // Validate last name on change
    if (lastName) {
      const validation = validateNameWithMessage(lastName, "Last name");
      setLastNameError(validation.error || null);
    } else {
      setLastNameError(null);
    }
  }, []);

  const updateEmail = useCallback((email: string) => {
    setCredentials((prev) => ({ ...prev, email }));
    // Validate email on change
    if (email) {
      const validation = validateEmailWithMessage(email);
      setEmailError(validation.error || null);
    } else {
      setEmailError(null);
    }
  }, []);

  const updatePassword = useCallback(
    (password: string) => {
      setCredentials((prev) => ({ ...prev, password }));
      // Validate password on change
      if (password) {
        const validation = validatePasswordWithMessage(password);
        setPasswordError(validation.error || null);
      } else {
        setPasswordError(null);
      }
      // Re-validate confirm password if it exists
      if (credentials.confirmPassword) {
        const confirmValidation = validatePasswordMatch(password, credentials.confirmPassword);
        setConfirmPasswordError(confirmValidation.error || null);
      }
    },
    [credentials.confirmPassword]
  );

  const updateConfirmPassword = useCallback(
    (confirmPassword: string) => {
      setCredentials((prev) => ({ ...prev, confirmPassword }));
      // Validate password match on change
      if (confirmPassword) {
        const validation = validatePasswordMatch(credentials.password, confirmPassword);
        setConfirmPasswordError(validation.error || null);
      } else {
        setConfirmPasswordError(null);
      }
    },
    [credentials.password]
  );

  const fullPhone = `${dialCode}${nationalNumber}`;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate all fields before submission
      const validation = validateSignupCredentials(
        credentials.firstName,
        credentials.lastName,
        credentials.email,
        credentials.password,
        credentials.confirmPassword
      );

      setFirstNameError(validation.firstNameError);
      setLastNameError(validation.lastNameError);
      setEmailError(validation.emailError);
      setPasswordError(validation.passwordError);
      setConfirmPasswordError(validation.confirmPasswordError);

      if (!validation.isValid) {
        return;
      }
      if (!nationalNumber) {
        setMobileError("Mobile number is required");
        return;
      }
      const phoneFormatError = getSignupPhoneFormatError(fullPhone);
      if (phoneFormatError) {
        setMobileError(phoneFormatError);
        return;
      }

      if (mobileError) {
        return;
      }

      mutation.mutate(credentials);
    },
    [credentials, mutation, mobileError, nationalNumber, fullPhone]
  );

  const reset = useCallback(() => {
    setCredentials({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      confirmPassword: "",
      phone: "",
    });
    setFirstNameError(null);
    setLastNameError(null);
    setEmailError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setMobileError(null);
  }, []);

  return {
    credentials,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    firstNameError,
    lastNameError,
    emailError,
    passwordError,
    confirmPasswordError,
    mobileError,
    updateFirstName,
    // updateMobile,
    updateLastName,
    updateEmail,
    updatePassword,
    updateConfirmPassword,
    handleSubmit,
    reset,
    dialCode,
    setDialCode,
    nationalNumber,
    updateNationalNumber,
  };
};
